import type { Translate } from 'next-translate';
import React from 'react';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import {
	ACCOUNT_SETTINGS_LINK,
	CONSTANTS,
	SUPPORT_MAIL_LINK,
} from '@/constants';
import { type LandingListFAQ } from '../../../../types';
import { AccordionItem } from './AccordionItem';
import styles from './Accordion.module.scss';

// Cancellation answer is the same on every landing, so it lives in the shared
// landing-config namespace and is appended to each page's own FAQ list
const SUBSCRIPTION_NAMESPACE = 'landing-config';
const SUBSCRIPTION_ITEM: LandingListFAQ = {
	title: 'faqSubscription_title',
	subtitle: 'faqSubscription_subtitle',
};

interface Props {
	items: LandingListFAQ[];
	t: Translate;
	/** Inline nodes for the answers' placeholders, keyed by the placeholder */
	links?: Record<string, React.ReactNode>;
	/** Pages that already ask about cancellation in their own list opt out */
	withSubscriptionItem?: boolean;
	variant?: 'default' | 'main';
}
export const Accordion: React.FC<Props> = ({
	items,
	t,
	links,
	withSubscriptionItem = true,
	variant = 'default',
}) => {
	const [expanded, setExpanded] = React.useState<number | null>(0);
	const { t: tShared } = useTranslationWithLocale(SUBSCRIPTION_NAMESPACE);

	const toggle = (index: number) => {
		setExpanded(prev => (prev === index ? null : index));
	};

	const visibleItems = items.filter(
		item => !item.optional || t(item.title) !== item.title,
	);

	// Skip it while the shared namespace is not resolved yet, so the key never leaks
	const hasSubscriptionItem =
		withSubscriptionItem &&
		tShared(SUBSCRIPTION_ITEM.title) !== SUBSCRIPTION_ITEM.title;

	// Keep the links clickable without toggling the item they sit in
	const stopToggle = (event: React.MouseEvent) => event.stopPropagation();
	const subscriptionLinks = {
		'{settings}': (
			<a
				href={ACCOUNT_SETTINGS_LINK}
				className={styles['accordion__link']}
				onClick={stopToggle}
			>
				{tShared('faqSubscription_settings')}
			</a>
		),
		'{email}': (
			<a
				href={SUPPORT_MAIL_LINK}
				className={styles['accordion__link']}
				onClick={stopToggle}
			>
				{CONSTANTS.SUPPORT_MAIL_ADDRESS}
			</a>
		),
	};

	return (
		<div className={styles['accordion']}>
			{visibleItems.map((item, index) => (
				<AccordionItem
					key={item.title}
					item={item}
					index={index}
					expanded={expanded}
					toggle={toggle}
					t={t}
					links={links}
					variant={variant}
				/>
			))}

			{hasSubscriptionItem && (
				<AccordionItem
					key={SUBSCRIPTION_ITEM.title}
					item={SUBSCRIPTION_ITEM}
					index={visibleItems.length}
					expanded={expanded}
					toggle={toggle}
					t={tShared}
					links={subscriptionLinks}
					variant={variant}
				/>
			)}
		</div>
	);
};
